import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { ContributionRequest } from '../../types/crowdfunding';
import { formatCurrency } from '../../utils/formatters';
import { useToast } from '../../hooks/useToast';
import { supabase } from '../../lib/supabase';
import { Loader2 } from 'lucide-react';

// Schéma de validation Zod adapté avec une sémantique de "don"
const donationSchema = z.object({
  amount: z.coerce.number().min(50, 'Le montant minimum du don est de 50 FCFA.'),
  contributor_name: z.string().min(2, 'Veuillez entrer votre nom.').max(100),
  contributor_email: z.string().email('Veuillez entrer un email valide.').optional().or(z.literal('')),
  contributor_phone: z.string().min(9, 'Veuillez entrer un numéro de téléphone valide.').max(15).optional().or(z.literal('')),
  message: z.string().max(500, 'Le message ne peut pas dépasser 500 caractères.').optional().or(z.literal('')),
  is_anonymous: z.boolean().default(false)
});

type DonationFormValues = z.infer<typeof donationSchema>;

interface ContributionFormProps {
  campaignSlug: string;
  onSuccess: (data: ContributionRequest) => void;
  isLoading?: boolean;
  className?: string;
  suggestedAmounts?: number[];
  gridCols?: number;
}

export const ContributionForm: React.FC<ContributionFormProps> = ({
  campaignSlug,
  onSuccess,
  isLoading = false,
  className = '',
  suggestedAmounts = [5000, 10000, 25000, 50000],
  gridCols = 2 // La prop est conservée pour la flexibilité
}) => {
  const { toast } = useToast();
  const [customAmount, setCustomAmount] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);

  const form = useForm<DonationFormValues>({
    resolver: zodResolver(donationSchema),
    defaultValues: {
      amount: 0,
      contributor_name: '',
      contributor_email: '',
      contributor_phone: '',
      message: '',
      is_anonymous: false
    }
  });

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount(false);
    form.setValue('amount', amount, { shouldValidate: true });
  };

  const handleCustomAmountToggle = () => {
    setCustomAmount(true);
    setSelectedAmount(null);
    form.setValue('amount', 0);
  };

  const onSubmit = async (data: DonationFormValues) => {
    if (data.amount < 50) {
      form.setError("amount", { type: "manual", message: "Le montant minimum est de 50 FCFA." });
      return;
    }

    try {
      const { data: campaignData } = await supabase
        .from('crowdfunding_campaigns')
        .select('id')
        .eq('slug', campaignSlug)
        .single();

      if (!campaignData) {
        throw new Error(`Aucun projet trouvé avec le slug ${campaignSlug}`);
      }

      const donationData: ContributionRequest = {
        campaign_id: campaignData.id,
        ...data
      };

      onSuccess(donationData);
    } catch (error) {
      console.error('Erreur lors de la préparation du don:', error);
      toast({
        title: "Erreur",
        description: "Impossible de préparer votre don. Veuillez réessayer.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className={`bg-transparent p-0 ${className}`}>
      {/* Le titre est maintenant géré par le composant parent pour plus de flexibilité */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {/* Section des montants suggérés avec la nouvelle palette */}
          <div className="space-y-3">
            <FormLabel className="text-sm font-semibold text-gray-300">Choisissez un montant</FormLabel>
            <div className={`grid grid-cols-${gridCols} gap-3`}> 
              {suggestedAmounts.map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant="outline"
                  className={`w-full py-3 h-auto text-base font-bold rounded-lg shadow-sm transition-all duration-200 border-2
                    ${selectedAmount === amount 
                      ? 'bg-red-600 border-red-500 text-white scale-105 shadow-lg shadow-red-900/40' 
                      : 'bg-black/20 border-white/20 text-gray-200 hover:bg-white/10 hover:border-red-500/50 hover:text-white'}
                  `}
                  onClick={() => handleAmountSelect(amount)}
                >
                  {formatCurrency(amount)}
                </Button>
              ))}
            </div>
            <div className="flex items-center mt-2">
              <Button
                type="button"
                variant="link"
                className={`text-sm ${customAmount ? 'text-red-400 font-bold' : 'text-gray-400 hover:text-white'}`}
                onClick={handleCustomAmountToggle}
              >
                Ou entrer un montant personnalisé
              </Button>
            </div>
          </div>

          {customAmount && (
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-300">Montant Personnalisé (FCFA)</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="Ex: 7500" className="bg-black/20 border-white/20 text-white placeholder-gray-500 focus:ring-red-500" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField control={form.control} name="contributor_name" render={({ field }) => (
            <FormItem><FormLabel className="text-gray-300">Votre nom ou celui de votre organisation</FormLabel><FormControl><Input placeholder="Ex: Jean Dupont" className="bg-black/20 border-white/20 text-white placeholder-gray-500 focus:ring-red-500" {...field} /></FormControl><FormMessage /></FormItem>
          )}/>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField control={form.control} name="contributor_email" render={({ field }) => (
              <FormItem><FormLabel className="text-gray-300">Email (pour le reçu)</FormLabel><FormControl><Input placeholder="adresse@email.com" className="bg-black/20 border-white/20 text-white placeholder-gray-500 focus:ring-red-500" {...field} /></FormControl><FormMessage /></FormItem>
            )}/>
            <FormField control={form.control} name="contributor_phone" render={({ field }) => (
              <FormItem><FormLabel className="text-gray-300">Téléphone (optionnel)</FormLabel><FormControl><Input placeholder="Votre numéro" className="bg-black/20 border-white/20 text-white placeholder-gray-500 focus:ring-red-500" {...field} /></FormControl><FormMessage /></FormItem>
            )}/>
          </div>

          <FormField control={form.control} name="message" render={({ field }) => (
            <FormItem><FormLabel className="text-gray-300">Message à l'artiste (optionnel)</FormLabel><FormControl><Textarea placeholder="Votre message de soutien..." className="bg-black/20 border-white/20 text-white placeholder-gray-500 focus:ring-red-500" {...field} /></FormControl><FormMessage /></FormItem>
          )}/>

          <FormField control={form.control} name="is_anonymous" render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0 pt-2"><FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} className="data-[state=checked]:bg-red-600" /></FormControl><FormLabel className="text-gray-300 font-normal">Rester anonyme sur la liste publique des mécènes</FormLabel></FormItem>
          )}/>

          <Button type="submit" className="w-full text-lg font-bold py-6 bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-900/30" disabled={isLoading}>
            {isLoading ? <><Loader2 className="animate-spin mr-2 h-5 w-5" /> Traitement...</> : 'Soutenir le projet'}
          </Button>
        </form>
      </Form>
    </div>
  );
};